import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, ShieldCheck, Menu, X, Flag, PlusCircle, Bell } from "lucide-react";
import { useState } from "react";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/campionati", label: "Campionati" },
  { to: "/piloti", label: "Piloti" },
  { to: "/galleria", label: "Galleria" },
  { to: "/locandine", label: "Locandine" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const { isAdmin, isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const active = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const doLogout = () => { logout(); navigate("/"); setOpen(false); };

  return (
    <header className="sticky top-0 z-50 bg-zinc-950/70 backdrop-blur-xl border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
          <img src="/team-tuono-logo.png" alt="Team Tuono" className="h-9 w-9 object-contain rounded-sm" />
          <span className="flex flex-col leading-none">
            <span className="font-display font-bold text-lg tracking-tight text-zinc-50">
              TEAM <span className="text-yellow-400">TUONO</span>
            </span>
            <span className="mt-0.5 text-[9px] font-bold tracking-[0.25em] uppercase text-zinc-500">
              Championship App
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-xs font-bold tracking-[0.2em] uppercase transition-colors duration-200 ${
                active(l.to) ? "text-yellow-400" : "text-zinc-400 hover:text-zinc-50"
              }`}>
              {l.label}
            </Link>
          ))}

          {isAuthenticated && (
            <Link to="/campionati-tuoi" data-testid="nav-organize"
              className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 hover:text-cyan-300 transition-colors duration-200 flex items-center gap-1.5">
              <PlusCircle className="h-4 w-4" /> Campionati Tuoi
            </Link>
          )}
          {isAuthenticated && (
            <Link to="/notifiche" data-testid="nav-notifiche"
              className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 hover:text-cyan-300 transition-colors duration-200 flex items-center gap-1.5">
              <Bell className="h-4 w-4" /> Notifiche
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin" data-testid="nav-admin"
              className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 hover:text-cyan-300 transition-colors duration-200 flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4" /> Admin
            </Link>
          )}

          {isAuthenticated ? (
            <div className="flex items-center gap-3 pl-2 border-l border-zinc-800">
              <span className="text-xs font-bold text-zinc-300 max-w-[120px] truncate">{user.name}</span>
              <button onClick={doLogout} data-testid="logout-btn"
                className="text-zinc-400 hover:text-red-400 transition-colors duration-200" title="Esci">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <Link to="/accedi" data-testid="nav-login"
              className="text-xs font-bold tracking-[0.15em] uppercase bg-yellow-400 text-zinc-950 px-4 py-2 hover:bg-yellow-300 transition-colors duration-200 -skew-x-12">
              <span className="inline-block skew-x-12">Accedi</span>
            </Link>
          )}
        </nav>

        <button className="lg:hidden text-zinc-100" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-zinc-800 bg-zinc-950 px-4 py-4 flex flex-col gap-4">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase()}`} onClick={() => setOpen(false)} className="text-sm font-bold tracking-wide uppercase text-zinc-300">
              {l.label}
            </Link>
          ))}
          {isAuthenticated && <Link to="/campionati-tuoi" data-testid="mobile-nav-organize" onClick={() => setOpen(false)} className="text-sm font-bold uppercase text-cyan-400">Campionati Tuoi</Link>}
          {isAuthenticated && <Link to="/notifiche" data-testid="mobile-nav-notifiche" onClick={() => setOpen(false)} className="text-sm font-bold uppercase text-cyan-400">Notifiche</Link>}
          {isAdmin && <Link to="/admin" data-testid="mobile-nav-admin" onClick={() => setOpen(false)} className="text-sm font-bold uppercase text-cyan-400">Admin</Link>}
          {isAuthenticated ? (
            <button onClick={doLogout} data-testid="mobile-logout-btn" className="text-left text-sm font-bold uppercase text-red-400">Esci</button>
          ) : (
            <Link to="/accedi" data-testid="mobile-nav-login" onClick={() => setOpen(false)} className="text-sm font-bold uppercase text-yellow-400">Accedi</Link>
          )}
        </div>
      )}
    </header>
  );
}
